import { istTeam, tagesschluessel } from '@netcon-work/shared'
import { queryOptions, useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { useMemo } from 'react'
import { sitzungQuery } from './queries'
import { trpc } from './trpc'
import { useAufgabenAllerWelten } from './welt'

/**
 * **Für Mein Tag markieren – im Browser** ([Z-20](../../../../docs/phasen/zusatz-20-mein-tag-markieren.md)).
 *
 * Eine Abfrage, zwei Handlungen, beide optimistisch (Leitplanke 1). Gebaut wie der Schirm
 * (`mutationen.ts`, Abschnitt Z-03): die Markierung gehört niemandem sonst, also ändert sich
 * kein Pool, kein Status, kein Bestand – vorhergesagt wird nur diese eine Liste.
 *
 * Was eine Markierung *heute bedeutet*, rechnet `@netcon-work/shared` (`markierKlasse`,
 * `markierWort`, …). Hier steht nur, woher sie kommt und wie sie sich ändert.
 */

export const markierungQuery = queryOptions({
  queryKey: ['markierung', 'meine'],
  queryFn: () => trpc.markierung.meine.query(),
  /** Kurz wie beim Schirm: das zweite Gerät soll eine Markierung bald sehen, ohne Live-Kanal. */
  staleTime: 10_000,
})

type Markierungen = Awaited<ReturnType<typeof trpc.markierung.meine.query>>

/**
 * **Meine Markierungen als Nachschlagetabelle** `taskId → Tag`.
 *
 * Für Mit-Owner und Gäste immer leer, und ohne Aufruf: sie haben kein „Mein Tag" (E-109), und
 * der Endpunkt ist eine `teamProcedure`.
 */
export function useMarkierungen(): Map<string, string> {
  const { data: sitzung } = useQuery(sitzungQuery)
  const imTeam = sitzung ? istTeam(sitzung.role) : false
  const { data } = useQuery({ ...markierungQuery, enabled: imTeam })
  return useMemo(() => new Map((data ?? []).map((m) => [m.taskId, m.am])), [data])
}

/**
 * **Die fest gesetzten Uhrzeiten** `taskId → { am, um }` – `um` in Minuten seit Mitternacht
 * (13.10.2026). Nur, was im Kalender an eine Uhrzeit gezogen wurde; alles andere legt der Plan.
 *
 * Eine eigene Tafel statt eines breiteren Werts in {@link useMarkierungen}: dort lesen ein
 * Dutzend Stellen nur den Tag, und die sollen davon nichts merken.
 */
export function useMarkierUhrzeiten(): Map<string, { am: string; um: number }> {
  const { data: sitzung } = useQuery(sitzungQuery)
  const imTeam = sitzung ? istTeam(sitzung.role) : false
  const { data } = useQuery({ ...markierungQuery, enabled: imTeam })
  return useMemo(
    () =>
      new Map(
        (data ?? []).flatMap((m) =>
          m.um === null || m.um === undefined ? [] : [[m.taskId, { am: m.am, um: m.um }] as const],
        ),
      ),
    [data],
  )
}

/**
 * **Nur, was noch zu tun ist.** Eine erledigte oder zurückgestellte Aufgabe behält ihre Zeile
 * (wird sie wieder geöffnet, ist die Absicht noch da), zählt aber in keiner Ansicht mit – nicht
 * im Lastbalken, nicht im Ausblick, nicht im Filter.
 */
export function useOffeneMarkierungen(): Map<string, string> {
  const markierungen = useMarkierungen()
  const { aufgaben } = useAufgabenAllerWelten()
  return useMemo(() => {
    const offen = new Map<string, string>()
    for (const a of aufgaben) {
      const am = markierungen.get(a.id)
      if (am && a.status !== 'erledigt' && a.status !== 'zurueckgestellt') offen.set(a.id, am)
    }
    return offen
  }, [markierungen, aufgaben])
}

/** Darf diese Sitzung markieren? Dieselbe Grenze wie am Endpunkt, nur sichtbar. */
export function useDarfMarkieren(): boolean {
  const { data: sitzung } = useQuery(sitzungQuery)
  return sitzung ? istTeam(sitzung.role) : false
}

/**
 * **Heute, als Tagesschlüssel.** Bei jedem Zeichnen neu gelesen – wie überall in „Mein Tag"
 * (`WiedervorlageMarke`, `Abwesenheit`). Wer über Mitternacht offen hat, bekommt den neuen Tag
 * mit dem nächsten Neuzeichnen, und das kommt spätestens mit der nächsten Abfrage.
 */
export function heuteSchluessel(): string {
  return tagesschluessel(new Date())
}

/**
 * Setzen (`am` = Tag) oder entfernen (`am` = `null`) – für eine oder viele Aufgaben in einem Zug.
 * `um` legt die Aufgabe an diesem Tag auf eine feste Uhrzeit (Minuten seit Mitternacht); ohne
 * `um` ist die Uhrzeit wieder frei.
 *
 * Bei einem Fehler wird auf den Stand vor dem Klick zurückgesetzt; der Fehler bleibt an der
 * Mutation, damit die Ansicht ihn zeigen kann.
 */
export function useMarkieren() {
  const qc = useQueryClient()

  return useMutation({
    mutationFn: ({ taskIds, am, um }: { taskIds: string[]; am: string | null; um?: number }) =>
      am === null
        ? trpc.markierung.entfernen.mutate({ taskIds })
        : trpc.markierung.setzen.mutate(um === undefined ? { taskIds, am } : { taskIds, am, um }),
    onMutate: async ({ taskIds, am, um }) => {
      await qc.cancelQueries({ queryKey: markierungQuery.queryKey })
      const vorher = qc.getQueryData<Markierungen>(markierungQuery.queryKey)
      const ids = new Set(taskIds)
      qc.setQueryData<Markierungen>(markierungQuery.queryKey, (alt) => {
        const rest = (alt ?? []).filter((m) => !ids.has(m.taskId))
        return am === null
          ? rest
          : [...rest, ...taskIds.map((taskId) => ({ taskId, am, um: um ?? null }))]
      })
      return vorher
    },
    onError: (_fehler, _w, vorher) => qc.setQueryData(markierungQuery.queryKey, vorher),
    /**
     * **Danach neu holen** (05.10.2026, im Browser gefunden). Anders als die Pins hat die
     * Markierung keinen Live-Kanal, der das übernähme – und eine Abfrage, die zwischen Klick und
     * Speichern startet (etwa weil Zeilen neu aufgebaut werden), brachte den alten Stand zurück
     * und überschrieb die Vorhersage. Die Liste ist eine Handvoll Zeilen; das kostet nichts.
     */
    onSettled: () => qc.invalidateQueries({ queryKey: markierungQuery.queryKey }),
  })
}

/**
 * **„Öffne den Kalender bei Mittwoch"** – aus der Ausblickzeile im Vorlauf.
 *
 * Der Tagesplan hält seinen Tag in eigenem Zustand und wird beim Umschalten neu aufgebaut. Statt
 * den Zustand nach oben zu ziehen, legt der Vorlauf den Wunsch hier ab, und der Tagesplan holt
 * ihn beim Aufbau **einmal** ab. Im `sessionStorage`, damit ein Neuladen nicht Tage später
 * noch bei Mittwoch landet.
 */
const KALENDER_TAG = 'netcon-work:kalender-tag'

export function kalenderTagVormerken(tag: string): void {
  try {
    window.sessionStorage.setItem(KALENDER_TAG, tag)
  } catch {
    // Ohne Speicher öffnet der Kalender eben bei heute.
  }
}

export function kalenderTagAbholen(): string | null {
  try {
    const tag = window.sessionStorage.getItem(KALENDER_TAG)
    window.sessionStorage.removeItem(KALENDER_TAG)
    return tag
  } catch {
    return null
  }
}
